import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { KeyRound, Trash2 } from 'lucide-react';
import { Button } from '@shared/components';
import { getCourierSetting } from '../../state/courierSlice';
import { usePasskeyManagement } from '../hooks/usePasskeyManagement';

const formatDate = (value?: Date | string) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
};

/**
 * Manage the signed-in user's passkeys.
 *
 * Renders nothing at all when the courier has the feature off, so it can be
 * dropped into the Security card unconditionally.
 */
const PasskeyManager = () => {
  const courierSetting = useSelector(getCourierSetting);
  const { credentials, isSupported, isLoading, isBusy, error, addPasskey, remove } =
    usePasskeyManagement();
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

  if (!courierSetting?.isPasskeyEnabled) return null;

  const handleRemove = async (credentialId: string) => {
    await remove(credentialId);
    setPendingRemoval(null);
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-1">
      <div className="flex items-center mb-2">
        <KeyRound className="h-4 w-4 text-gray-400 mr-2" />
        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
          Passkeys
        </h4>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Sign in with your fingerprint, face or screen lock instead of a password.
      </p>

      {error && (
        <div
          role="alert"
          className="mb-3 rounded-md bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {!isSupported ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          This browser does not support passkeys.
        </p>
      ) : (
        <>
          {isLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Loading your passkeys…
            </p>
          ) : credentials.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              No passkeys yet.
            </p>
          ) : (
            <ul className="space-y-2 mb-3">
              {credentials.map((credential) => {
                const credentialId = credential.credentialId as string;
                const lastUsed = formatDate(credential.lastUsedAt);
                const added = formatDate(credential.createdOn);

                return (
                  <li
                    key={credentialId}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {credential.name || 'Passkey'}
                        </p>
                        {/* The domain matters: a courier that moves domains
                            leaves its users' old passkeys unusable, and this
                            is the only place that is visible. */}
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {credential.rpId}
                          {added ? ` · added ${added}` : ''}
                          {lastUsed ? ` · last used ${lastUsed}` : ' · never used'}
                        </p>
                      </div>

                      {pendingRemoval === credentialId ? (
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            variant="danger"
                            onClick={() => handleRemove(credentialId)}
                            disabled={isBusy}
                          >
                            Confirm removal
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={() => setPendingRemoval(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="secondary"
                          onClick={() => setPendingRemoval(credentialId)}
                          disabled={isBusy}
                        >
                          <Trash2 className="h-4 w-4 mr-1 inline" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <Button
            variant="secondary"
            fullWidth
            onClick={() => addPasskey()}
            disabled={isBusy}
            isLoading={isBusy}
          >
            Add a passkey
          </Button>
        </>
      )}
    </div>
  );
};

export default PasskeyManager;
